import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLinkProps } from '@react-navigation/native';

import colors from 'config/colors';
import { SortingOrders } from 'config/constants';
import { useScreenContext } from 'navigation/context/ScreenContextProvider';
import { RouteNames } from 'navigation/linkingConfig';
import { AppSelectors } from 'store/App';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { OrderActions, OrderSelectors } from 'store/Orders';
import { SettingActions, SettingSelectors } from 'store/Settings';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { Button, Picker, Text } from 'components';

import { NavigationButtonSecondary } from './NavigationButtons';
import { commonStyles } from './styles';

const SORT_OPTIONS = [
  {
    label: 'sideBar.filterSection.optionByOrderAge',
    value: SortingOrders.BY_PLACED_AT_ASC,
  },
  {
    label: 'sideBar.filterSection.optionByPromisedTime',
    value: SortingOrders.BY_PROMISED_TIME_AT_ASC,
  },
  {
    label: 'sideBar.filterSection.optionByPriorityTime',
    value: SortingOrders.BY_START_PREPARATION_STATUS_DESC,
    featureFlagKey: 'kds_enabled_traffic_light_system',
  },
];

const DELIVERY_ONLY_COLOR = colors.ui.orderKinds.delivery;
const DELIVERY_ONLY_ACTIVE_COUNT_COLOR = `${colors.white}40`;
const DELIVERY_ONLY_INACTIVE_COUNT_COLOR = `${DELIVERY_ONLY_COLOR}40`;

function DeliveryOnly() {
  const { t } = useTranslation();
  const { screen } = useScreenContext();
  const dispatch = useAppDispatch();
  const logEvent = useLogEventCallback();

  const isSidebarMinimized = useAppSelector(
    AppSelectors.selectIsSidebarMinimized,
  );
  const isDeliveryOnlyEnabled = useAppSelector(
    OrderSelectors.selectIsDeliveryOnlyEnabled,
  );
  const deliveryOrderCountByScreen = useAppSelector(
    OrderSelectors.selectDeliveryOrderCountByScreen,
  );
  const deliveryOrderCount = deliveryOrderCountByScreen[screen];

  const label = t('sideBar.filterSection.deliveryOnly');
  const foregroundColor = isDeliveryOnlyEnabled
    ? colors.white
    : DELIVERY_ONLY_COLOR;
  const accessibilityProps = {
    accessibilityLabel: `${label}: ${deliveryOrderCount}`,
    accessibilityRole: 'button' as const,
    accessibilityState: { selected: isDeliveryOnlyEnabled },
  };

  return (
    <Button
      {...accessibilityProps}
      testID="delivery-only-filter"
      onPress={() => {
        dispatch(OrderActions.toggleDeliveryOnly());
        logEvent(
          AnalyticsEvents.deliveryOnlyFilterUpdated({
            enabled: !isDeliveryOnlyEnabled,
          }),
        );
      }}
      color={isDeliveryOnlyEnabled ? DELIVERY_ONLY_COLOR : colors.darkBlue}
      innerColor={foregroundColor}
      variant={isDeliveryOnlyEnabled ? 'solid' : 'outline'}
      style={styles.deliveryOnlyButton}
      innerStyle={styles.deliveryOnlyButtonInner}
      textStyle={styles.deliveryOnlyLabel}
      font="bold"
      size={16}
      title={isSidebarMinimized ? undefined : label}
    >
      <View
        style={[
          styles.deliveryOnlyCount,
          isDeliveryOnlyEnabled
            ? styles.deliveryOnlyCountActive
            : styles.deliveryOnlyCountInactive,
          isSidebarMinimized && styles.deliveryOnlyCountMinimized,
        ]}
      >
        <Text size={14} font="bold" color={foregroundColor}>
          {deliveryOrderCount}
        </Text>
      </View>
    </Button>
  );
}

function Ordering() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const logEvent = useLogEventCallback();

  const isSidebarMinimized = useAppSelector(
    AppSelectors.selectIsSidebarMinimized,
  );

  const sortingOrder = useAppSelector(
    SettingSelectors.selectOrdersSortingOrder,
  );

  const setSelectedSortingOrder = (order) => {
    dispatch(SettingActions.setOrdersSorting(order));
    logEvent(AnalyticsEvents.orderSortingUpdated({ sortBy: order }));
  };

  return (
    <Picker
      value={sortingOrder}
      onChange={setSelectedSortingOrder}
      options={SORT_OPTIONS}
      renderTrigger={({ onOpen, selectedOption }) => (
        <NavigationButtonSecondary
          onPress={onOpen}
          iconName="sort"
          isMinimized={isSidebarMinimized}
          label={t(
            !isSidebarMinimized
              ? selectedOption.label
              : `${selectedOption.label}_short`,
          )}
        />
      )}
    />
  );
}

function Categories() {
  const { t } = useTranslation();

  const isSidebarMinimized = useAppSelector(
    AppSelectors.selectIsSidebarMinimized,
  );

  const categories = useAppSelector(SettingSelectors.selectAllCategories);
  const selectedCategories = useAppSelector(
    SettingSelectors.selectSelectedCategories,
  );
  const isSelectedAllCategories = useAppSelector(
    SettingSelectors.selectIsSelectedAllCategories,
  );

  const navigate = useLinkProps({
    to: {
      screen: RouteNames.Home.Settings._,
      params: {
        screen: RouteNames.Home.Settings.Categories,
      },
    },
  });

  const translationKey = isSidebarMinimized
    ? 'sideBar.filterSection.categories_short'
    : 'sideBar.filterSection.categories';

  const count = isSelectedAllCategories
    ? categories.length
    : selectedCategories.length;

  return (
    <NavigationButtonSecondary
      iconName="food"
      label={t(translationKey, {
        count,
        total: categories.length,
      })}
      onPress={() => {
        navigate.onPress();
      }}
      isMinimized={isSidebarMinimized}
    />
  );
}

function OrderKinds() {
  const { t } = useTranslation();

  const isSidebarMinimized = useAppSelector(
    AppSelectors.selectIsSidebarMinimized,
  );

  const orderKinds = useAppSelector(SettingSelectors.selectAllOrderKinds);
  const selectedOrderKinds = useAppSelector(
    SettingSelectors.selectSelectedOrderKinds,
  );
  const isSelectedAllOrderKinds = useAppSelector(
    SettingSelectors.selectIsSelectedAllOrderKinds,
  );

  const navigate = useLinkProps({
    to: {
      screen: RouteNames.Home.Settings._,
      params: {
        screen: RouteNames.Home.Settings.OrderKinds,
      },
    },
  });

  const translationKey = isSidebarMinimized
    ? 'sideBar.filterSection.orderKinds_short'
    : 'sideBar.filterSection.orderKinds';
  const count = isSelectedAllOrderKinds
    ? orderKinds.length
    : selectedOrderKinds.length;

  return (
    <NavigationButtonSecondary
      iconName="silverware-variant"
      label={t(translationKey, {
        count,
        total: orderKinds.length,
      })}
      onPress={() => {
        navigate.onPress();
      }}
      isMinimized={isSidebarMinimized}
    />
  );
}

export function FilterSection() {
  return (
    <View style={commonStyles.container}>
      <DeliveryOnly />
      <OrderKinds />
      <Categories />
      <Ordering />
    </View>
  );
}

const styles = StyleSheet.create({
  deliveryOnlyButton: {
    borderColor: DELIVERY_ONLY_COLOR,
    borderRadius: 8,
    borderWidth: 2,
    marginTop: 8,
    minHeight: 46,
  },
  deliveryOnlyButtonInner: {
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  deliveryOnlyCount: {
    alignItems: 'center',
    borderRadius: 14,
    height: 28,
    justifyContent: 'center',
    marginLeft: 12,
    minWidth: 28,
    paddingHorizontal: 8,
  },
  deliveryOnlyCountActive: {
    backgroundColor: DELIVERY_ONLY_ACTIVE_COUNT_COLOR,
  },
  deliveryOnlyCountInactive: {
    backgroundColor: DELIVERY_ONLY_INACTIVE_COUNT_COLOR,
  },
  deliveryOnlyCountMinimized: {
    marginLeft: 0,
  },
  deliveryOnlyLabel: {
    marginLeft: 0,
  },
});
